import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRAPHICS_PERSON_OPTIONS = ["Michael", "Alyssa"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium mb-2">{label}</span>
    {children}
  </label>
);

const inputClass = "w-full px-3 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring";

export const ProjectInlineEditorV53 = ({ project, customers, onClose, onSaved }) => {
  const firstSku = project?.sku_items?.[0] || {};
  const [manufacturers, setManufacturers] = useState([]);
  const [repOptions, setRepOptions] = useState({ isr_list: [], osr_list: [] });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: project?.name || "",
    customer_id: project?.customer_id || "",
    customer_contact_name:
      project?.customer_contact?.name || "",
    customer_contact_email:
      project?.customer_contact?.email || "",
    customer_contact_phone:
      project?.customer_contact?.phone || "",
    manufacturer_id: project?.manufacturer_id || "",
    description: project?.description || "",
    priority: project?.priority || "medium",
    deadline: (project?.deadline || project?.target_due_date || "").split("T")[0],
    isr: project?.isr || "",
    osr: project?.osr || "",
    graphics_person: project?.graphics_person || project?.assigned_graphics_person || "",
    copacker: project?.copacker || project?.filler || "",
    brand: project?.brand || "",
    can_size: project?.can_size || "",
    print_type: project?.print_type || "",
    decoration_method: project?.decoration_method || "",
    beverage_type: project?.beverage_type || "",
    beer_type: project?.beer_type || "",
    gamer_po: project?.gamer_po || "",
    customer_po: project?.customer_po || "",
    sku_name: project?.sku_name || firstSku.sku_name || "",
    pasteurization_range: project?.pasteurization_range || firstSku.pasteurization_range || "Not Required",
    specialty_ink_type: project?.specialty_ink_type || firstSku.specialty_ink_type || "None",
    overvarnish_type: project?.overvarnish_type || firstSku.overvarnish_type || "",
    inside_varnish_content: project?.inside_varnish_content || firstSku.inside_varnish || "",
    comments: project?.comments || firstSku.sku_notes || "",
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/manufacturers`),
      axios.get(`${API}/public/intake-options`),
    ]).then(([manufacturerResponse, optionResponse]) => {
      setManufacturers(manufacturerResponse.data || []);
      setRepOptions(optionResponse.data || { isr_list: [], osr_list: [] });
    }).catch(() => {});
  }, []);

  const update = (name, value) => setForm((previous) => ({ ...previous, [name]: value }));

  const updatePrintType = (value) => {
    setForm((previous) => ({
      ...previous,
      print_type: value,
      decoration_method: String(value || "").toLowerCase().includes("digital") ? previous.decoration_method : "",
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.customer_id) {
      toast.error("Project name and customer are required");
      return;
    }
    setSaving(true);
    try {
      const skuItems = project?.sku_items?.length
        ? project.sku_items.map((sku, index) => index === 0 ? {
            ...sku,
            sku_name: form.sku_name,
            pasteurization_range: form.pasteurization_range,
            specialty_ink_type: form.specialty_ink_type,
            overvarnish_type: form.overvarnish_type,
            inside_varnish: form.inside_varnish_content,
            sku_notes: form.comments,
          } : sku)
        : [{
            sku_name: form.sku_name,
            pasteurization_range: form.pasteurization_range,
            specialty_ink_type: form.specialty_ink_type,
            overvarnish_type: form.overvarnish_type,
            inside_varnish: form.inside_varnish_content,
            sku_notes: form.comments,
          }];

      const response = await axios.put(`${API}/projects/${project.id}`, {
        ...form,
        name: form.name.trim(),
        assigned_graphics_person: form.graphics_person,
        customer_id: form.customer_id,
        customer_contact: {
          name: form.customer_contact_name.trim(),
          email: form.customer_contact_email.trim(),
          phone: form.customer_contact_phone.trim(),
        },
        manufacturer_id: form.manufacturer_id || null,
        deadline: form.deadline || null,
        sku_items: skuItems,
      });
      toast.success("Project updated");
      onSaved(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const isDigitalPrint = String(form.print_type || "").toLowerCase().includes("digital");

  return (
    <section id="project-inline-editor" className="rounded-xl border border-primary/40 bg-card" aria-label="Edit project details">
      <div>
        <div className="flex items-center justify-between p-5 bg-card border-b border-border">
          <div>
            <h2 className="text-xl font-bold">Edit Project Details</h2>
            <p className="text-sm text-muted-foreground">Edit details by section, then save your changes. Unsaved edits remain in the form until you save or cancel.</p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="p-2 rounded-lg hover:bg-secondary" aria-label="Cancel project editing">
            <X size={19} />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-6">

        <section
          className="mb-6 rounded-lg border border-border bg-secondary/20 p-4"
          data-testid="customer-contact-edit-v48"
        >
          <h3 className="mb-3 text-sm font-semibold">
            Customer Contact
          </h3>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Contact Name">
              <input
                type="text"
                className={inputClass}
                value={form.customer_contact_name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    customer_contact_name:
                      event.target.value,
                  })
                }
              />
            </Field>

            <Field label="Contact Email">
              <input
                type="email"
                className={inputClass}
                value={form.customer_contact_email}
                onChange={(event) =>
                  setForm({
                    ...form,
                    customer_contact_email:
                      event.target.value,
                  })
                }
              />
            </Field>

            <Field label="Contact Phone">
              <input
                type="text"
                className={inputClass}
                value={form.customer_contact_phone}
                onChange={(event) =>
                  setForm({
                    ...form,
                    customer_contact_phone:
                      event.target.value,
                  })
                }
              />
            </Field>
          </div>
        </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Project Name">
              <input className={inputClass} value={form.name} onChange={(event) => update("name", event.target.value)} required />
            </Field>
            <Field label="Customer">
              <select className={inputClass} value={form.customer_id} onChange={(event) => update("customer_id", event.target.value)} required>
                <option value="">Select customer...</option>
                {(customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
              </select>
            </Field>
            <Field label="Manufacturer">
              <select className={inputClass} value={form.manufacturer_id} onChange={(event) => update("manufacturer_id", event.target.value)}>
                <option value="">No manufacturer</option>
                {manufacturers.map((manufacturer) => <option key={manufacturer.id} value={manufacturer.id}>{manufacturer.name}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className={inputClass} value={form.priority} onChange={(event) => update("priority", event.target.value)}>
                {['low', 'medium', 'high', 'urgent'].map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Sales Ops">
              <select className={inputClass} value={form.isr} onChange={(event) => update("isr", event.target.value)}>
                <option value="">Unassigned</option>
                {(repOptions.isr_list || []).map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Field>
            <Field label="Outside Sales Rep">
              <select className={inputClass} value={form.osr} onChange={(event) => update("osr", event.target.value)}>
                <option value="">Unassigned</option>
                {(repOptions.osr_list || []).map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Field>
            <Field label="Graphics Person">
              <select className={inputClass} value={form.graphics_person} onChange={(event) => update("graphics_person", event.target.value)}>
                <option value="">Unassigned</option>
                {GRAPHICS_PERSON_OPTIONS.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Field>
            <Field label="Filler"><input className={inputClass} value={form.copacker} onChange={(event) => update("copacker", event.target.value)} /></Field>
            <Field label="Brand"><input className={inputClass} value={form.brand} onChange={(event) => update("brand", event.target.value)} /></Field>
            <Field label="Can Size"><input className={inputClass} value={form.can_size} onChange={(event) => update("can_size", event.target.value)} /></Field>
            <Field label="Need By Date"><input type="date" className={inputClass} value={form.deadline} onChange={(event) => update("deadline", event.target.value)} /></Field>
            <Field label="Print Type">
              <select className={inputClass} value={form.print_type} onChange={(event) => updatePrintType(event.target.value)}>
                <option value="">Select...</option>
                <option value="Digital Printing">Digital Printing</option>
                <option value="Offset Printing">Offset Printing</option>
              </select>
            </Field>
            {isDigitalPrint && (
              <Field label="Decoration Method">
                <select className={inputClass} value={form.decoration_method} onChange={(event) => update("decoration_method", event.target.value)}>
                  <option value="">Select...</option>
                  <option value="Pressure Sensitive Label">Pressure Sensitive Label</option>
                  <option value="Shrink Sleeve">Shrink Sleeve</option>
                </select>
              </Field>
            )}
            <Field label="Artwork PO"><input className={inputClass} value={form.gamer_po} onChange={(event) => update("gamer_po", event.target.value)} /></Field>
            <Field label="Customer PO"><input className={inputClass} value={form.customer_po} onChange={(event) => update("customer_po", event.target.value)} /></Field>
            <Field label="SKU / Label Name"><input className={inputClass} value={form.sku_name} onChange={(event) => update("sku_name", event.target.value)} /></Field>
            <Field label="Pasteurization"><input className={inputClass} value={form.pasteurization_range} onChange={(event) => update("pasteurization_range", event.target.value)} /></Field>
            <Field label="Specialty Ink"><input className={inputClass} value={form.specialty_ink_type} onChange={(event) => update("specialty_ink_type", event.target.value)} /></Field>
            <Field label="Overvarnish"><input className={inputClass} value={form.overvarnish_type} onChange={(event) => update("overvarnish_type", event.target.value)} /></Field>
            <Field label="Inside Varnish"><input className={inputClass} value={form.inside_varnish_content} onChange={(event) => update("inside_varnish_content", event.target.value)} /></Field>
          </div>

          <Field label="Description">
            <textarea className={inputClass} rows={3} value={form.description} onChange={(event) => update("description", event.target.value)} />
          </Field>
          <Field label="Comments / Remarks">
            <textarea className={inputClass} rows={4} value={form.comments} onChange={(event) => update("comments", event.target.value)} />
          </Field>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" disabled={saving} onClick={onClose} className="px-4 py-2.5 bg-secondary rounded-lg">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50">
              {saving ? "Saving..." : "Save Project"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};